import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BriefcaseBusiness, CalendarClock, CircleDollarSign, CreditCard, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { api, customerAuthHeaders } from '../lib/api';
import { clearCustomerToken } from '../lib/session';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

function statusLabel(status, lang) {
  const ru = {
    awaiting_deposit: 'Ожидает депозита',
    deposit_pending: 'Депозит в обработке',
    deposit_paid: 'Депозит оплачен',
    final_payment_pending: 'Полная оплата в обработке',
    fully_paid: 'Тур оплачен',
    completed: 'Завершён',
  };
  const en = {
    awaiting_deposit: 'Awaiting deposit',
    deposit_pending: 'Deposit pending',
    deposit_paid: 'Deposit paid',
    final_payment_pending: 'Final payment pending',
    fully_paid: 'Fully paid',
    completed: 'Completed',
  };
  const map = lang === 'en' ? en : ru;
  return map[status] || status;
}

function paymentStatusLabel(status, lang) {
  const ru = { unpaid: 'Не оплачен', pending: 'В обработке', paid: 'Оплачен' };
  const en = { unpaid: 'Unpaid', pending: 'Pending', paid: 'Paid' };
  return (lang === 'en' ? en : ru)[status] || status;
}

export default function AccountDashboard() {
  const { i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingStage, setPayingStage] = useState('');

  const copy = useMemo(() => (
    i18n.language === 'en'
      ? {
        title: 'My Bookings',
        subtitle: 'Track your tour reservations, deposit, and final payment status.',
        logout: 'Sign out',
        noBookings: 'You do not have any bookings yet.',
        deposit: 'Deposit',
        final: 'Final payment',
        startPayment: 'Start payment',
        awaitingAmount: 'Amount will be confirmed by manager',
        support: 'Payment provider integration is prepared in the data model. Real checkout URL will be connected in the next step.',
      }
      : {
        title: 'Мои бронирования',
        subtitle: 'Отслеживайте брони туров, депозит и статус полной оплаты.',
        logout: 'Выйти',
        noBookings: 'У вас пока нет бронирований.',
        deposit: 'Депозит',
        final: 'Полная оплата',
        startPayment: 'Перейти к оплате',
        awaitingAmount: 'Сумму подтвердит менеджер',
        support: 'Модель оплаты уже подготовлена. Реальный checkout URL будет подключён следующим шагом интеграции.',
      }
  ), [i18n.language]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [meResponse, bookingsResponse] = await Promise.all([
        api.get('/customer/me', customerAuthHeaders()),
        api.get('/customer/bookings', customerAuthHeaders()),
      ]);
      setProfile(meResponse.data);
      setBookings(Array.isArray(bookingsResponse.data) ? bookingsResponse.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || (i18n.language === 'en' ? 'Failed to load account' : 'Не удалось загрузить кабинет'));
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    clearCustomerToken();
    window.location.href = '/';
  };

  const handleStartPayment = async (bookingId, stage) => {
    setPayingStage(`${bookingId}:${stage}`);
    try {
      const response = await api.post(`/customer/bookings/${bookingId}/payments/start`, { stage }, customerAuthHeaders());
      toast.success(
        i18n.language === 'en'
          ? `Payment session created: ${response.data.checkout_reference}`
          : `Сессия оплаты создана: ${response.data.checkout_reference}`,
      );
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || (i18n.language === 'en' ? 'Failed to start payment' : 'Не удалось начать оплату'));
    } finally {
      setPayingStage('');
    }
  };

  return (
    <div className="pt-32 pb-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="glass rounded-3xl p-8 md:p-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="space-y-2">
            <div className="text-white/50 text-sm">{profile?.email}</div>
            <h1 className="font-heading text-4xl md:text-5xl font-black text-white">{copy.title}</h1>
            <p className="text-white/70 max-w-3xl">{copy.subtitle}</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 rounded-full">
            <LogOut size={16} className="mr-2" />
            {copy.logout}
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Metric icon={BriefcaseBusiness} title={i18n.language === 'en' ? 'Bookings' : 'Брони'} value={bookings.length} />
          <Metric icon={CreditCard} title={copy.deposit} value={bookings.filter((item) => item.deposit?.status === 'paid').length} />
          <Metric icon={CircleDollarSign} title={copy.final} value={bookings.filter((item) => item.final_payment?.status === 'paid').length} />
        </div>

        <div className="rounded-2xl border border-[#E11D2E]/20 bg-[#E11D2E]/5 p-4 text-sm text-white/70">
          {copy.support}
        </div>

        {loading ? (
          <div className="text-white/60">{i18n.language === 'en' ? 'Loading...' : 'Загрузка...'}</div>
        ) : bookings.length === 0 ? (
          <div className="glass rounded-3xl p-8 text-white/60">{copy.noBookings}</div>
        ) : (
          <div className="grid gap-6">
            {bookings.map((booking) => (
              <Card key={booking.id} className="glass border-white/10 bg-black/20 text-white">
                <CardHeader className="gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <CardTitle className="text-2xl font-heading font-black">
                      {i18n.language === 'en' && booking.tour_title_en ? booking.tour_title_en : booking.tour_title}
                    </CardTitle>
                    <CardDescription className="text-white/60">
                      <span className="inline-flex items-center gap-2 mr-4"><CalendarClock size={14} />{booking.preferred_start_date || (i18n.language === 'en' ? 'Date not selected' : 'Дата не выбрана')}</span>
                      <span>{booking.participants} {i18n.language === 'en' ? 'participant(s)' : 'участник(ов)'}</span>
                    </CardDescription>
                  </div>
                  <Badge className="bg-[#E11D2E]/15 text-[#ff8f98] border-[#E11D2E]/20">{statusLabel(booking.status, i18n.language)}</Badge>
                </CardHeader>
                <CardContent className="grid gap-4 lg:grid-cols-2">
                  <PaymentCard
                    lang={i18n.language}
                    title={copy.deposit}
                    payment={booking.deposit}
                    loading={payingStage === `${booking.id}:deposit`}
                    startLabel={copy.startPayment}
                    emptyLabel={copy.awaitingAmount}
                    onStart={() => handleStartPayment(booking.id, 'deposit')}
                  />
                  <PaymentCard
                    lang={i18n.language}
                    title={copy.final}
                    payment={booking.final_payment}
                    loading={payingStage === `${booking.id}:final`}
                    startLabel={copy.startPayment}
                    emptyLabel={copy.awaitingAmount}
                    onStart={() => handleStartPayment(booking.id, 'final')}
                  />
                  {booking.notes ? (
                    <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="text-white/50 text-sm mb-2">{i18n.language === 'en' ? 'Notes' : 'Комментарий'}</div>
                      <div className="text-white/80 whitespace-pre-line">{booking.notes}</div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, title, value }) {
  return (
    <div className="glass rounded-2xl p-5 border border-white/10">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-[#E11D2E]/15 text-[#E11D2E] flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div className="text-white/60 text-sm">{title}</div>
      </div>
      <div className="text-3xl font-heading font-black text-white">{value}</div>
    </div>
  );
}

function PaymentCard({ lang, title, payment, loading, startLabel, emptyLabel, onStart }) {
  const amount = payment?.amount;
  const canStart = payment?.status !== 'paid';

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-white font-semibold">{title}</div>
        <Badge className="bg-white/10 text-white border-white/10">{paymentStatusLabel(payment?.status || 'unpaid', lang)}</Badge>
      </div>
      <div className="text-2xl font-heading font-black text-white">
        {amount ? `${amount} ${payment?.currency || 'RUB'}` : emptyLabel}
      </div>
      {payment?.checkout_reference ? (
        <div className="text-xs text-white/50">{payment.checkout_reference}</div>
      ) : null}
      {canStart ? (
        <Button onClick={onStart} disabled={loading} className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full">
          {loading ? '...' : startLabel}
        </Button>
      ) : null}
    </div>
  );
}
